import React, { useEffect, useState } from 'react';
import { AlertCircle, Users } from 'react-feather';

// Components
import EventDate from './EventDate';
import EventPreviewDialogue from './EventPreviewDialogue';
import ThriveCheckBox from '../global/Checkbox';

// Hooks
import useEventColour from  '@/components/hooks/useEventColour';

// Utils and Services
import { endOfDayTime, removeTime } from  '@/helpers/Dates';
import { BuildPath } from  '@/helpers/Paths';

// Interfaces
import { ToastData } from  '@/interfaces/LockerDetails';
import { plannerEvent } from  '@/interfaces/planner';



interface DaySingleProps {
    date:Date,
    events:plannerEvent[],
    plusEvent:(date:Date) => void,
    setToast:(toastData:ToastData) => void,
    selectedEvents: number[],
    setSelectedEvents: (id:number) => void,
    hoverTargetId: number,
    setHoverTargetId: (id:number) => void
}

const DaySingle = ({date, events, plusEvent, setToast, selectedEvents, setSelectedEvents, hoverTargetId, setHoverTargetId}: DaySingleProps) => {

    const [singleEvents, setSingleEvents] = useState<plannerEvent[]>([])
    const [isToday, setIsToday] = useState<boolean>(false)
    const [showAdd, setShowAdd] = useState<boolean>(false)
    const [showPreviewDialogue, setShowPreviewDialogue]= useState<boolean[]>([])

    const {cellStyle} = useEventColour()

    useEffect(() => {

        // Only show add icon for today and future
        if (!date) return 

        const now = new Date();

        const isCurrentYear = date.getFullYear() === now.getFullYear()


        if(!isCurrentYear && date.getFullYear() > now.getFullYear()) {
            setShowAdd(true) 
        }
        else if(isCurrentYear && date.getMonth() > now.getMonth()) {
            setShowAdd(true)
        }
        else if(isCurrentYear && date.getMonth() === now.getMonth() && date.getDate() >= now.getDate()){
            setShowAdd(true)
        }
        else{
            setShowAdd(false)
        }
        

    }, [date])

    useEffect(() => {

        if (!events.length) {
            setSingleEvents([])
        }
        else {
            const startOfDay = removeTime(date)
            const endOfDay = endOfDayTime(date)

            // Single day events
            const dayEvents = events.filter(x => x.occurrence.start >= startOfDay && x.occurrence.end <= endOfDay).sort((a,b)=> {
                if (a.occurrence.start > b.occurrence.start){
                    return 1
                }
                return -1
            } );

            setSingleEvents(dayEvents)
        }
    }, [events, date])

    useEffect(() => {
        if(!showPreviewDialogue.some(el => el)) {
            setShowPreviewDialogue(events.map(() => false))
        }
    }, [events])

    

    useEffect(() => {

        // Hightlight if its today
        const today = new Date();

        if (today.getFullYear() === date.getFullYear() 
            && today.getMonth() === date.getMonth() 
            && today.getDate() === date.getDate()){  
            setIsToday(true)
        }
        else{
            setIsToday(false)
        }

    },[date])

    function getAlignment() {
        switch(date.getDay()){
            case 0:
                return 'right-align';
            case 6: 
                return 'center-align';
            default:
                return 'left-align'
        }
    }

    function openModal(i: number) {
        setShowPreviewDialogue(showPreviewDialogue.map((ev, idx) => idx === i))
    }

    function closeOnBlur(i: number){
        if(!showPreviewDialogue[i]){
            setShowPreviewDialogue(showPreviewDialogue.map(() => false))
        }
    }

    function showCheckbox(evt: plannerEvent) {
        return hoverTargetId === evt.id || !!selectedEvents.length   
    }

    return (
        <li className={`one-day ${isToday ? 'today':''} ${[0, 6].includes(date.getDay()) ? 'weekend' : ''}`}>

            {singleEvents.map((evt, i) => {
                return (
                    
                <div 
                    role='button'
                    key={i} 
                    className={`event ${evt.occurrence.occurrenceType.toLowerCase()} ${(evt.isCancelled) ? 'cancelled' : ''}`} 
                    style={!evt.isCancelled ? cellStyle : {}}
                    onClick={()=> openModal(i)}
                    onFocus={() => closeOnBlur(i)}
                    onMouseEnter={() => setHoverTargetId(evt.id)}
                    onMouseLeave={() => setHoverTargetId(0)}
                >

                    <div className='single-day-title-wrapper'>
                        <h3 className='title-text'>{evt.title}</h3>
                        {showCheckbox(evt) && 
                            <ThriveCheckBox
                                id={`select-${evt.title}-${evt.id}`}
                                value={selectedEvents.includes(evt.id)}
                                change={() => setSelectedEvents(evt.id)}
                                name={`add ${evt.title} to download report`}
                            />
                        }
                    </div>
                    
                     {/* hidden screen reader only button */}
                     <button
                        aria-label={`${evt.title} view details`}
                        className={'sr-only'}
                        onClick={()=> openModal(i)}
                        onFocus={() => closeOnBlur(i)}
                    />

                    {evt.isCancelled  && 
                        <span className='cancelled-text'><AlertCircle size={12} style={{marginRight: '2px', marginTop: '2px'}}/>Cancelled</span>
                    }

                    <EventDate event={evt} />

                    {evt.awaitingApprovalCount > 0 && 
                        <span className='single-awaiting-approval-text'>
                            <Users size={12} style={{marginRight:'5px'}}/>
                            {`${evt.awaitingApprovalCount} Awaiting Approval`}
                        </span>
                    }

                    {showPreviewDialogue[i] && 
                        <EventPreviewDialogue
                            onClose={() => setShowPreviewDialogue(events.map(() => false))} 
                            evtId={evt.id}
                            alignment={getAlignment()}
                            setToast={setToast}
                        />
                    }

                </div>    
                )
            })}

            {showAdd && 
                <button className='add' aria-label={`add event: ${date.toDateString()}`} onClick={() => plusEvent(date)}>
                    <img src={`${BuildPath()}/images/planner/plus.svg`} alt="Add" />
                </button>
            }

        </li>
    );
};

export default DaySingle;
